import React, { useState, useEffect, useCallback } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { useApp } from "../context/AppContext";
import api from "../api/client";
import { CheckCircle2, XCircle, Clock, X, Calendar, User, FileText, Plus, Minus, ShieldAlert } from "lucide-react";

const FILTERS = ["Pending", "All", "Approved", "Rejected"];

function countDays(from, to) {
  if (!from || !to) return 0;
  const f = new Date(from), t = new Date(to);
  if (isNaN(f) || isNaN(t)) return 0;
  return Math.max(0, Math.round((t - f) / 86400000) + 1);
}

export function ManagerLeaveScreen() {
  const { fetchLeaves, updateLeave } = useApp();
  const [leaves, setLeaves] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Pending");
  const [rejectModal, setRejectModal] = useState(null); // { id }
  const [rejectReason, setRejectReason] = useState("");
  const [actioningId, setActioningId] = useState(null);
  const [editModal, setEditModal] = useState(null); // { employee_id, emp_name, cl_total, cl_used, ml_total, ml_used }
  const [updatingEmpId, setUpdatingEmpId] = useState(null);
  
  // Allocate Leave Modal State
  const [allocateModalOpen, setAllocateModalOpen] = useState(false);
  const [allocateData, setAllocateData] = useState({
    employeeId: "",
    leaveType: "CL",
    from: "",
    to: "",
    reason: ""
  });
  const [allocating, setAllocating] = useState(false);

  const handleSaveManualBalance = async () => {
    if (!editModal) return;
    setUpdatingEmpId(editModal.employee_id);
    try {
      await api.patch(`/leaves/balance/${editModal.employee_id}`, {
        cl_total: editModal.cl_total,
        cl_used: editModal.cl_used,
        ml_total: editModal.ml_total,
        ml_used: editModal.ml_used,
      });
      setEditModal(null);
      await loadLeaves();
    } catch (err) {
      console.error("Manual balance save error:", err);
      alert(err.response?.data?.error || "Failed to update leave balance");
    } finally {
      setUpdatingEmpId(null);
    }
  };

  const loadLeaves = useCallback(async () => {
    try {
      const [res, balRes] = await Promise.all([
        fetchLeaves(),
        api.get("/leaves/balance").catch(() => ({ data: { data: [] } }))
      ]);
      setLeaves(res.data || []);
      setBalances(balRes.data?.data || []);
    } catch (err) {
      console.error("Error fetching leaves:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchLeaves]);

  useEffect(() => {
    loadLeaves();
    const timer = setInterval(loadLeaves, 4000); // sync every 4s
    return () => clearInterval(timer);
  }, [loadLeaves]);

  const handleAdjustCL = async (employeeId, action, amount = 1) => {
    setUpdatingEmpId(employeeId);
    try {
      await api.patch(`/leaves/balance/${employeeId}`, { action, amount });
      await loadLeaves();
    } catch (err) {
      console.error("Adjust CL error:", err);
      alert(err.response?.data?.error || "Failed to update CL balance");
    } finally {
      setUpdatingEmpId(null);
    }
  };

  const handleApprove = async (id) => {
    setActioningId(id);
    try {
      await updateLeave(id, "approved");
      await loadLeaves();
    } catch (err) {
      console.error(err);
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActioningId(rejectModal.id);
    try {
      await updateLeave(rejectModal.id, "rejected", rejectReason);
      setRejectModal(null);
      setRejectReason("");
      await loadLeaves();
    } catch (err) {
      console.error(err);
    } finally {
      setActioningId(null);
    }
  };

  const handleAllocateSubmit = async (e) => {
    e.preventDefault();
    if (!allocateData.employeeId || !allocateData.from || !allocateData.to) {
      alert("Please select employee and date range");
      return;
    }
    setAllocating(true);
    try {
      await api.post("/leaves/allocate", allocateData);
      setAllocateModalOpen(false);
      setAllocateData({ employeeId: "", leaveType: "CL", from: "", to: "", reason: "" });
      await loadLeaves();
      alert("Leave allocated successfully");
    } catch (err) {
      console.error("Allocate error:", err);
      alert(err.response?.data?.error || "Failed to allocate leave");
    } finally {
      setAllocating(false);
    }
  };

  const displayed = leaves.filter((l) => {
    if (filter === "All") return true;
    if (filter === "Pending") return l.status === "pending";
    if (filter === "Approved") return l.status === "approved";
    if (filter === "Rejected") return l.status === "rejected";
    return true;
  });

  const pendingCount = leaves.filter((l) => l.status === "pending").length;
  const approvedCount = leaves.filter((l) => l.status === "approved").length;

  return (
    <div>
      {/* Summary Row */}
      <div style={{ display: "flex", gap: 10 }}>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.warning }}>
            {pendingCount}
          </div>
          <div style={{ fontSize: 10, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5 }}>PENDING LEAVE REQUESTS</div>
        </Card>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.success }}>
            {approvedCount}
          </div>
          <div style={{ fontSize: 10, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5 }}>APPROVED LEAVES</div>
        </Card>
      </div>

      {/* Allocate Leave Button */}
      <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={() => setAllocateModalOpen(true)}
          style={{
            background: TOKENS.navyDeep,
            color: TOKENS.gold,
            border: "none",
            padding: "10px 16px",
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
          }}
        >
          <Calendar size={16} /> Allocate Leave for Employee
        </button>
      </div>

      {/* Casual Leave (CL) Balance Control Panel */}
      <div style={{ marginTop: 18, marginBottom: 18 }}>
        <SectionLabel>🌴 Casual Leave (CL) Allocation & Management</SectionLabel>
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: TOKENS.muted, marginBottom: 12 }}>
            Manage and adjust total Casual Leave (CL) balances for each employee.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {balances.length === 0 ? (
              <div style={{ fontSize: 12, color: TOKENS.muted, fontStyle: "italic", textAlign: "center", padding: 12 }}>
                Loading employee leave balances…
              </div>
            ) : (
              balances.map((b) => {
                const clTotal = b.cl_total ?? 6;
                const clUsed = b.cl_used ?? 0;
                const clRemaining = clTotal - clUsed;
                const isUpdating = updatingEmpId === b.employee_id;

                return (
                  <div
                    key={b.employee_id}
                    style={{
                      background: TOKENS.cream,
                      border: `1.5px solid ${TOKENS.border}`,
                      borderRadius: 12,
                      padding: "12px 14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13.5, color: TOKENS.navyDeep }}>
                        {b.emp_name || b.employee_id}
                      </div>
                      <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2 }}>
                        Emp ID: <strong>{b.employee_id}</strong> · Used CL: <strong>{clUsed}</strong>
                      </div>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: clRemaining > 0 ? TOKENS.success : TOKENS.danger, marginTop: 3 }}>
                        CL Balance: {clRemaining} / {clTotal} Days Remaining
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <button
                        onClick={() => handleAdjustCL(b.employee_id, "add_cl", 1)}
                        disabled={isUpdating}
                        style={{
                          background: TOKENS.successBg,
                          border: `1px solid ${TOKENS.success}50`,
                          color: TOKENS.success,
                          borderRadius: 8,
                          padding: "6px 12px",
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          opacity: isUpdating ? 0.6 : 1,
                        }}
                      >
                        <Plus size={14} /> Add CL
                      </button>

                      <button
                        onClick={() => handleAdjustCL(b.employee_id, "delete_cl", 1)}
                        disabled={isUpdating || clTotal <= 0}
                        style={{
                          background: TOKENS.dangerBg,
                          border: `1px solid ${TOKENS.danger}50`,
                          color: TOKENS.danger,
                          borderRadius: 8,
                          padding: "6px 12px",
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          opacity: (isUpdating || clTotal <= 0) ? 0.5 : 1,
                        }}
                      >
                        <Minus size={14} /> Delete CL
                      </button>

                      <button
                        onClick={() => setEditModal({
                          employee_id: b.employee_id,
                          emp_name: b.emp_name || b.employee_id,
                          cl_total: clTotal,
                          cl_used: clUsed,
                          ml_total: b.ml_total ?? 6,
                          ml_used: b.ml_used ?? 0,
                          statusPreset: clTotal >= 12 ? "permanent" : clTotal >= 6 ? "probation" : clTotal >= 4 ? "contract" : "custom",
                        })}
                        style={{
                          background: TOKENS.cream,
                          border: `1px solid ${TOKENS.navyDeep}40`,
                          color: TOKENS.navyDeep,
                          borderRadius: 8,
                          padding: "6px 12px",
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        ⚙️ Edit Allocation
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>

      {/* Filter Chips */}
      <div style={{ display: "flex", gap: 8, margin: "14px 0", overflowX: "auto" }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "6px 14px",
              borderRadius: 20,
              background: filter === f ? TOKENS.navyDeep : "#fff",
              color: filter === f ? "#fff" : TOKENS.muted,
              border: `1.5px solid ${filter === f ? TOKENS.navyDeep : TOKENS.border}`,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Applications List */}
      <SectionLabel>Employee Leave Applications</SectionLabel>

      {loading && leaves.length === 0 ? (
        <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 24 }}>
          Loading leave applications…
        </Card>
      ) : displayed.length === 0 ? (
        <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 24 }}>
          No {filter.toLowerCase()} leave applications found.
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {displayed.map((l) => {
            const numDays = countDays(l.from_date || l.from, l.to_date || l.to);
            return (
              <Card key={l.id} style={{ padding: 16 }}>
                {/* Header: Employee & Status */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        background: TOKENS.navyMid,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {l.initials || l.emp_name?.slice(0, 2)?.toUpperCase() || "EP"}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14.5, color: TOKENS.navyDeep }}>
                        {l.emp_name || l.employee_id}
                      </div>
                      <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 1 }}>
                        Emp ID: {l.employee_id} {l.cl_total !== undefined ? `· CL: ${l.cl_total - l.cl_used}/${l.cl_total} · Comp Off: ${(l.ml_total ?? 6) - (l.ml_used ?? 0)}/${l.ml_total ?? 6}` : ""}
                      </div>
                    </div>
                  </div>
                  <StatusPill status={l.status} />
                </div>

                {/* Details */}
                <div
                  style={{
                    background: TOKENS.cream,
                    borderRadius: 10,
                    padding: 12,
                    marginTop: 12,
                    border: `1.5px solid ${TOKENS.border}`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep }}>
                      {(() => {
                        const t = (l.leave_type || l.leaveType || "CL").toUpperCase();
                        if (t === "ML") return "🏥 Comp Off Leave";
                        if (t === "LOP") return "💸 Loss of Pay (LOP)";
                        return "🌴 Casual Leave (CL)";
                      })()}
                    </span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: TOKENS.gold, background: `${TOKENS.navyDeep}12`, padding: "2px 8px", borderRadius: 6 }}>
                      {numDays} {numDays === 1 ? "Day" : "Days"}
                    </span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: TOKENS.ink, marginBottom: 6 }}>
                    <Calendar size={13} color={TOKENS.muted} />
                    <span>
                      <strong>{l.from_date || l.from}</strong> to <strong>{l.to_date || l.to}</strong>
                    </span>
                  </div>

                  {l.reason && (
                    <div style={{ fontSize: 12, color: TOKENS.ink, fontStyle: "italic", borderTop: `1px dashed ${TOKENS.border}`, paddingTop: 6, marginTop: 6 }}>
                      "{l.reason}"
                    </div>
                  )}

                  {(l.documentUrl || l.document_path) && (
                    <div style={{ borderTop: `1px dashed ${TOKENS.border}`, paddingTop: 8, marginTop: 8 }}>
                      <a
                        href={l.documentUrl || getImageUrl(l.document_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12,
                          fontWeight: 700, color: TOKENS.navyDeep, background: "#fff",
                          padding: "6px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.navyDeep}30`,
                          textDecoration: "none"
                        }}
                      >
                        📄 View Attached Document / Medical Certificate
                      </a>
                    </div>
                  )}
                </div>

                {l.rejectReason && (
                  <div style={{ fontSize: 11.5, color: TOKENS.danger, marginTop: 8, fontWeight: 600 }}>
                    Rejection Reason: {l.rejectReason}
                  </div>
                )}

                {/* Approve / Reject Actions */}
                {l.status === "pending" && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      onClick={() => handleApprove(l.id)}
                      disabled={actioningId === l.id}
                      style={{
                        flex: 1,
                        background: TOKENS.success,
                        color: "#fff",
                        border: "none",
                        borderRadius: 10,
                        padding: "9px 0",
                        fontWeight: 700,
                        fontSize: 12.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        cursor: "pointer",
                        opacity: actioningId === l.id ? 0.6 : 1,
                      }}
                    >
                      <CheckCircle2 size={15} /> Approve
                    </button>
                    <button
                      onClick={() => {
                        setRejectModal({ id: l.id });
                        setRejectReason("");
                      }}
                      disabled={actioningId === l.id}
                      style={{
                        flex: 1,
                        background: "#fff",
                        color: TOKENS.danger,
                        border: `1.5px solid ${TOKENS.danger}`,
                        borderRadius: 10,
                        padding: "9px 0",
                        fontWeight: 700,
                        fontSize: 12.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        cursor: "pointer",
                        opacity: actioningId === l.id ? 0.6 : 1,
                      }}
                    >
                      <XCircle size={15} /> Reject
                    </button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
      {/* Reject Modal */}
      {rejectModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10,21,45,0.5)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 16,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              padding: 24,
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 24px 60px rgba(10,25,50,0.22)",
              border: `1.5px solid ${TOKENS.border}`,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: TOKENS.navyDeep }}>
                Reject Leave Application
              </div>
              <button onClick={() => setRejectModal(null)} style={{ border: "none", background: "none", cursor: "pointer" }}>
                <X size={18} color={TOKENS.muted} />
              </button>
            </div>
            <textarea
              placeholder="Provide reason for rejecting leave request…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`,
                fontSize: 13,
                outline: "none",
                background: TOKENS.cream,
                resize: "none",
                marginBottom: 14,
              }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setRejectModal(null)}
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: `1.5px solid ${TOKENS.border}`,
                  background: "#fff",
                  color: TOKENS.ink,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                style={{
                  flex: 1,
                  background: TOKENS.danger,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual CL Allocation & Status Modal */}
      {editModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(10,20,40,0.65)", display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 999, padding: 16
        }}>
          <div style={{
            background: "#fff", borderRadius: 20, padding: 22, maxWidth: 440, width: "100%",
            boxShadow: "0 20px 40px rgba(0,0,0,0.25)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div>
                <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                  Manual CL & Leave Allocation
                </h3>
                <div style={{ fontSize: 12, color: TOKENS.muted, marginTop: 2 }}>
                  Employee: <strong>{editModal.emp_name}</strong> ({editModal.employee_id})
                </div>
              </div>
              <button onClick={() => setEditModal(null)} style={{ background: "transparent", border: "none", cursor: "pointer", color: TOKENS.muted }}>
                <X size={18} />
              </button>
            </div>

            {/* Quick Status Presets */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: TOKENS.navyDeep, display: "block", marginBottom: 6 }}>
                Employment Status Preset:
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  { key: "permanent", label: "Permanent (12 CL/yr)", cl: 12, ml: 6 },
                  { key: "probation", label: "Probation (6 CL/yr)", cl: 6, ml: 6 },
                  { key: "contract", label: "Contract (4 CL/yr)", cl: 4, ml: 2 },
                  { key: "trainee", label: "Trainee (2 CL/yr)", cl: 2, ml: 1 },
                ].map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => setEditModal((prev) => ({
                      ...prev,
                      cl_total: preset.cl,
                      ml_total: preset.ml,
                      statusPreset: preset.key,
                    }))}
                    style={{
                      padding: "8px 10px", borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                      background: editModal.statusPreset === preset.key ? TOKENS.navyDeep : TOKENS.cream,
                      color: editModal.statusPreset === preset.key ? "#fff" : TOKENS.ink,
                      border: `1.5px solid ${editModal.statusPreset === preset.key ? TOKENS.navyDeep : TOKENS.border}`,
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Manual Inputs Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.navyDeep, display: "block", marginBottom: 4 }}>
                  Casual Leave Total (CL):
                </label>
                <input
                  type="number"
                  min="0"
                  value={editModal.cl_total}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, cl_total: parseInt(e.target.value, 10) || 0, statusPreset: "custom" }))}
                  style={{ width: "100%", padding: 8, borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, fontWeight: 700 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.navyDeep, display: "block", marginBottom: 4 }}>
                  CL Used Days:
                </label>
                <input
                  type="number"
                  min="0"
                  value={editModal.cl_used}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, cl_used: parseInt(e.target.value, 10) || 0 }))}
                  style={{ width: "100%", padding: 8, borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, fontWeight: 700 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.navyDeep, display: "block", marginBottom: 4 }}>
                  Comp Off Total:
                </label>
                <input
                  type="number"
                  min="0"
                  value={editModal.ml_total}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, ml_total: parseInt(e.target.value, 10) || 0 }))}
                  style={{ width: "100%", padding: 8, borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, fontWeight: 700 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.navyDeep, display: "block", marginBottom: 4 }}>
                  Comp Off Used Days:
                </label>
                <input
                  type="number"
                  min="0"
                  value={editModal.ml_used}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, ml_used: parseInt(e.target.value, 10) || 0 }))}
                  style={{ width: "100%", padding: 8, borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, fontWeight: 700 }}
                />
              </div>
            </div>

            <div style={{ background: `${TOKENS.navyDeep}08`, borderRadius: 10, padding: 10, marginBottom: 16, fontSize: 12, color: TOKENS.navyDeep, fontWeight: 600 }}>
              Calculated Remaining Balance: <strong>{Math.max(0, editModal.cl_total - editModal.cl_used)} CL Days</strong> remaining
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setEditModal(null)}
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, background: "#fff", color: TOKENS.ink, fontWeight: 700, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveManualBalance}
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", background: TOKENS.navyDeep, color: "#fff", fontWeight: 700, cursor: "pointer" }}
              >
                Save Allocation
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Allocation Modal */}
      {/* ...existing edit modal... */}

      {/* Allocate Leave Modal */}
      {allocateModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex",
          alignItems: "center", justifyContent: "center", padding: 20
        }}>
          <div style={{
            background: "#fff", width: "100%", maxWidth: 450, borderRadius: 16,
            padding: 24, boxShadow: "0 10px 30px rgba(0,0,0,0.2)"
          }}>
            <h3 style={{ fontFamily: "Fraunces, serif", margin: "0 0 16px", color: TOKENS.navyDeep }}>
              Allocate Leave
            </h3>
            <form onSubmit={handleAllocateSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep }}>Select Employee</label>
                <select
                  value={allocateData.employeeId}
                  onChange={(e) => setAllocateData({ ...allocateData, employeeId: e.target.value })}
                  style={{ padding: 10, borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13 }}
                  required
                >
                  <option value="">-- Select Employee --</option>
                  {balances.map(b => (
                    <option key={b.employee_id} value={b.employee_id}>
                      {b.emp_name || b.employee_id} (ID: {b.employee_id})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep }}>Leave Type</label>
                  <select
                    value={allocateData.leaveType}
                    onChange={(e) => setAllocateData({ ...allocateData, leaveType: e.target.value })}
                    style={{ padding: 10, borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13 }}
                  >
                    <option value="CL">Casual Leave (CL)</option>
                    <option value="ML">Comp Off Leave</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep }}>From Date</label>
                  <input
                    type="date"
                    required
                    value={allocateData.from}
                    onChange={(e) => setAllocateData({ ...allocateData, from: e.target.value })}
                    style={{ padding: 10, borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13 }}
                  />
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep }}>To Date</label>
                  <input
                    type="date"
                    required
                    value={allocateData.to}
                    onChange={(e) => setAllocateData({ ...allocateData, to: e.target.value })}
                    style={{ padding: 10, borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13 }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep }}>Reason (Optional)</label>
                <textarea
                  value={allocateData.reason}
                  onChange={(e) => setAllocateData({ ...allocateData, reason: e.target.value })}
                  placeholder="Enter reason for allocation..."
                  style={{ padding: 10, borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, minHeight: 60 }}
                />
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setAllocateModalOpen(false)}
                  style={{ flex: 1, padding: 10, borderRadius: 8, border: `1px solid ${TOKENS.border}`, background: "#fff", fontWeight: 700, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={allocating}
                  style={{ flex: 1, padding: 10, borderRadius: 8, border: "none", background: TOKENS.navyDeep, color: TOKENS.gold, fontWeight: 700, cursor: "pointer" }}
                >
                  {allocating ? "Allocating..." : "Allocate & Approve"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
